import React, { FC } from 'react';
import { Filter } from 'components/molecules/filters/types';
import { useTranslation } from 'hooks/use-translation';
import { text } from '../combiner';
import { Criteria } from '../criteria';
import { EditorComponents } from '../criteria/editor-components';
import { getDefaultCriteria } from '../criteria/get-default-criteria';
import {
  BooleanField,
  DateField,
  FieldType,
  FilterableField,
  MultiLineTextField,
  MultiSelectField,
  MultiUserField,
  NoteField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from '../fields';
import {
  BooleanFieldFilter,
  BooleanFilterOperationType,
} from '../filters/boolean';
import { DateFieldFilter, DateFieldFilterOperationType } from '../filters/date';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { MultiUserFieldFilter } from '../filters/multi-user';
import {
  NumberFieldFilter,
  NumberFilterOperationType,
} from '../filters/number';
import { SelectFilterOperationType } from '../filters/select';
import { SingleSelectFieldFilter } from '../filters/single-select';
import { SingleUserFieldFilter } from '../filters/single-user';
import { TextFieldFilter, TextFieldFilterOperationType } from '../filters/text';
import { AddFilterButton, AdvancedBoxWrapper, Row } from './components';

export interface GetFilterOperationTypeOptions {
  [FieldType.Date]?: (field: DateField) => DateFieldFilter[];
  [FieldType.Text]?: (
    field: TextField | MultiLineTextField | NoteField,
  ) => TextFieldFilter[];
  [FieldType.Number]?: (field: NumberField) => NumberFieldFilter[];
  [FieldType.Boolean]?: (field: BooleanField) => BooleanFieldFilter[];
  [FieldType.SingleSelect]?: (
    field: SingleUserField | SingleSelectField,
  ) => SingleUserFieldFilter[] | SingleSelectFieldFilter[];
  [FieldType.MultiSelect]?: (
    field: MultiUserField | MultiSelectField,
  ) => MultiUserFieldFilter[] | MultiSelectFieldFilter[];
}

export interface GetOperationOptionRenderer {
  BooleanFilterOperationType?: (
    type: BooleanFilterOperationType,
  ) => null | JSX.Element;
  NumberFilterOperationType?: (
    type: NumberFilterOperationType,
  ) => null | JSX.Element;
  TextFieldFilterOperationType?: (
    type: TextFieldFilterOperationType,
  ) => null | JSX.Element;
  SelectFilterOperationType?: (
    type: SelectFilterOperationType,
  ) => null | JSX.Element;
  DateFieldFilterOperationType?: (
    type: DateFieldFilterOperationType,
    filter?: DateFieldFilter,
  ) => null | JSX.Element;
}

export type GetSelectEditorComponents = (
  field:
    | SingleSelectField
    | MultiSelectField
    | SingleUserField
    | MultiUserField,
) => EditorComponents | undefined;

export interface Props {
  hideMatchTypeText?: boolean;
  addButtonText?: string;
  fields: FilterableField[];
  filter: Filter;
  onChange: (filter: Filter) => void;
  getFilterOperationTypeOptions?: GetFilterOperationTypeOptions;
  getSelectEditorComponents?: GetSelectEditorComponents;
  getOperationOptionRenderer?: GetOperationOptionRenderer;
  className?: string;
}

export const AdvancedFilterComponent: FC<Props> = ({
  fields,
  filter,
  onChange,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  hideMatchTypeText = false,
  addButtonText,
  getOperationOptionRenderer,
  className,
}) => {
  const default_text = useTranslation('task_list.filters.add');
  const add_filter = addButtonText ?? default_text;

  return (
    <div className={className}>
      <AdvancedBoxWrapper className="ssf-advanced-box">
        {filter.criteria.map((f, i) => (
          <Row key={`${f.field.fieldId}_${String(i)}`}>
            <Criteria
              hideMatchTypeText={hideMatchTypeText}
              onRemove={() =>
                onChange({
                  ...filter,
                  criteria: filter.criteria.filter((_, j) => i !== j),
                })
              }
              text={text(i, filter.type)}
              fields={fields}
              filter={f}
              getFilterOperationTypeOptions={getFilterOperationTypeOptions}
              getSelectEditorComponents={getSelectEditorComponents}
              onChange={(g) =>
                onChange({
                  ...filter,
                  criteria: filter.criteria.map((x, j) => (i !== j ? x : g)),
                })
              }
              getOperationOptionRenderer={getOperationOptionRenderer}
              index={i}
            />
          </Row>
        ))}
        {filter.criteria.length === 0 && (
          <Row>
            <Criteria
              hideMatchTypeText={hideMatchTypeText}
              text={text(filter.criteria.length, filter.type)}
              fields={fields}
              getFilterOperationTypeOptions={getFilterOperationTypeOptions}
              getSelectEditorComponents={getSelectEditorComponents}
              onChange={(g) =>
                onChange({ ...filter, criteria: filter.criteria.concat(g) })
              }
            />
          </Row>
        )}
      </AdvancedBoxWrapper>
      {filter.criteria.length > 0 && (
        <Row>
          <AddFilterButton
            data-cy="add.filter"
            onClick={() =>
              onChange({
                ...filter,
                criteria: filter.criteria.concat(getDefaultCriteria(fields[0])),
              })
            }
          >
            {add_filter}
          </AddFilterButton>
        </Row>
      )}
    </div>
  );
};
